import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  public_service_provider_api_requests,
  service_provider_api_requests,
  SERVICE_PROVIDER_API,
} from "..";
import { BaseDto, FilterGroup, NormalFilter } from "../../util/baseDto";
import { useExport } from "../../util/useExport";
import { useNotification } from "../../../components/common/GlobalNotification";
import { useMemo } from "react";

const PATH = "/service-area-locality";
const BASE_QUERY_KEY = SERVICE_PROVIDER_API + PATH;

export interface ServiceAreaLocalityItem extends BaseDto {
  id: number;
  locality: string;
  serviceAreaId: number;
  activeIndicator: boolean;
}

export interface ServiceAreaLocalityApiResponse {
  metaData: {
    size: number;
    totalPages: number;
    page: number;
    totalElements: number;
  };
  data: ServiceAreaLocalityItem[];
}

export interface CreateLocalityRequest {
  locality: string;
  serviceAreaId: number;
  activeIndicator?: boolean;
}

export interface UpdateLocalityRequest {
  locality: string;
  serviceAreaId: number;
  activeIndicator?: boolean;
}
export interface CreateLocalityResponse extends ServiceAreaLocalityItem {}

const useCreateServiceAreaLocality = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (
      request: CreateLocalityRequest
    ): Promise<CreateLocalityResponse> =>
      service_provider_api_requests.post(PATH, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Service area Locality created successfully");
    },
    onError: () => {
      setErrorNotification("Failed to create service area Locality");
    },
  });
};

const useUpdateServiceAreaLocality = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      id,
      request,
    }: {
      id: number;
      request: UpdateLocalityRequest;
    }): Promise<CreateLocalityResponse> =>
      service_provider_api_requests.put(`${PATH}/${id}`, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Service area Locality updated successfully");
    },
    onError: () => {
      setErrorNotification("Failed to update service area Locality");
    },
  });
};

const useSearchServiceAreasLocality = (
  size: number,
  page: number,
  serviceAreaId?: number,
  sort: string = "locality,asc"
) => {
  const filterGroup: FilterGroup = useMemo(
    () => ({
      operator: "AND",
      criteria: [
        ...(serviceAreaId
          ? [NormalFilter("serviceAreaId", "EQUALS", serviceAreaId)]
          : []),
      ],
    }),
    [serviceAreaId]
  );

  return useInfiniteQuery<ServiceAreaLocalityApiResponse, Error>({
    queryKey: [BASE_QUERY_KEY, "paged", serviceAreaId, size, sort],
    queryFn: async ({ pageParam = page }) =>
      public_service_provider_api_requests.search(
        `${PATH}/public/search?page=${pageParam}&size=${size}&sort=${sort}`,
        filterGroup
      ),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metaData.page ?? 0;
      const totalPages = lastPage.metaData.totalPages ?? 0;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!serviceAreaId,
  });
};

const useGetServiceAreasLocality = () => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "localities"],
    queryFn: async (): Promise<ServiceAreaLocalityApiResponse> =>
      public_service_provider_api_requests.get(PATH + "/public/all?size=100"),
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useGetServiceAreaLocalityById = (id: number) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "localities", id],
    queryFn: async (): Promise<ServiceAreaLocalityItem> =>
      public_service_provider_api_requests.get(`${PATH}/public/${id}`),
    enabled: !!id,
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
const useGetLocalitiesByServiceArea = (serviceAreaId?: number) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "by-service-area", serviceAreaId],
    queryFn: async (): Promise<ServiceAreaLocalityItem[]> =>
      public_service_provider_api_requests.get(
        `${PATH}/public/by-service-area/${serviceAreaId}`
      ),
    enabled: !!serviceAreaId,
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
const useDeleteServiceAreaLocality = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (id: number): Promise<void> =>
      service_provider_api_requests.delete(`${PATH}/${id}`),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Service area locality deleted successfully");
    },

    onError: () => {
      setErrorNotification("Failed to delete service area locality");
    },
  });
};
export const useExportServiceAreaLocalities = () =>
  useExport(
    (filterGroup) => public_service_provider_api_requests.export(`${PATH}/public/export`, filterGroup),
    "service-area-localities.xlsx",
    "Failed to export Service Area Localities",
  );

export {
  BASE_QUERY_KEY as SERVICE_AREA_LOCALITY_QUERY_KEY,
  useCreateServiceAreaLocality,
  useUpdateServiceAreaLocality,
  useSearchServiceAreasLocality,
  useGetServiceAreasLocality,
  useGetServiceAreaLocalityById,
  useGetLocalitiesByServiceArea,
  useDeleteServiceAreaLocality,
};
